import { useState, useCallback } from 'react';

/**
 * Custom hook for Marketing Projections
 * Allows users to play with variables and see projected outcomes
 */
export const useProjections = () => {
  const [projectionInputs, setProjectionInputs] = useState({
    traffic: 15000,
    visitorConversionRate: 1.3, // %
    mqlToSqlRate: 10, // %
    showUpRate: 60, // %
    closingRate: 25, // %
    avgSaleValue: 12000,
    costPerLead: 250,
    adSpend: 50000,
  });

  const [projectedResults, setProjectedResults] = useState(null);

  const calculateProjections = useCallback((inputs) => {
    const visitors = inputs.traffic;
    const mqls = Math.round(visitors * (inputs.visitorConversionRate / 100));
    const sqls = Math.round(mqls * (inputs.mqlToSqlRate / 100));
    const showUps = Math.round(sqls * (inputs.showUpRate / 100));
    const closes = Math.round(showUps * (inputs.closingRate / 100));
    
    const revenue = closes * inputs.avgSaleValue;
    const totalLeadCost = mqls * inputs.costPerLead;
    const roi = totalLeadCost > 0 ? ((revenue - totalLeadCost) / totalLeadCost) * 100 : 0;
    const roas = inputs.adSpend > 0 ? revenue / inputs.adSpend : 0;
    const cpa = closes > 0 ? inputs.adSpend / closes : 0;

    return {
      visitors,
      mqls,
      sqls,
      showUps,
      closes,
      revenue,
      totalCost: totalLeadCost,
      roi,
      roas,
      cpa,
    };
  }, []);

  const updateInput = useCallback((key, value) => {
    setProjectionInputs((prev) => {
      const updated = { ...prev, [key]: parseFloat(value) || 0 };
      setProjectedResults(calculateProjections(updated));
      return updated;
    });
  }, [calculateProjections]);

  const resetInputs = useCallback(() => {
    const defaults = {
      traffic: 15000,
      visitorConversionRate: 1.3,
      mqlToSqlRate: 10,
      showUpRate: 60,
      closingRate: 25,
      avgSaleValue: 12000,
      costPerLead: 250,
      adSpend: 50000,
    };
    setProjectionInputs(defaults);
    setProjectedResults(calculateProjections(defaults));
  }, [calculateProjections]);

  // Calculate initial results
  useState(() => {
    setProjectedResults(calculateProjections(projectionInputs));
  });

  return {
    inputs: projectionInputs,
    results: projectedResults,
    updateInput,
    resetInputs,
  };
};